/**
 * Swagger 对比模块
 * 用于比较两个 swagger 文件的差异
 */
import { SwaggerJSON } from '@/define';

/**
 * API 端点信息
 */
export interface ApiEndpoint {
  path: string; // API 路径
  method: string; // 请求方法
  summary?: string; // 摘要
  description?: string; // 描述
  parameters?: any[]; // 参数
  responses?: any; // 响应
}

/**
 * Swagger 对比结果
 */
export interface SwaggerComparisonResult {
  added: ApiEndpoint[]; // 新增的接口
  removed: ApiEndpoint[]; // 删除的接口
  modified: {
    endpoint: ApiEndpoint;
    changes: string[]; // 变化描述
  }[]; // 修改的接口
}

/**
 * 从 swagger 中提取所有 API 端点
 */
const extractApiEndpoints = (swagger: SwaggerJSON): ApiEndpoint[] => {
  const endpoints: ApiEndpoint[] = [];
  
  if (!swagger.paths) {
    return endpoints;
  }

  Object.keys(swagger.paths).forEach((path) => {
    const pathItem = (swagger.paths as any)[path];
    ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'].forEach((method) => {
      if (pathItem[method]) {
        const operation = pathItem[method] as any;
        endpoints.push({
          path,
          method: method.toUpperCase(),
          summary: operation.summary,
          description: operation.description,
          parameters: operation.parameters,
          responses: operation.responses,
        });
      }
    });
  });

  return endpoints;
};

/**
 * 生成 API 端点的唯一标识
 */
const getEndpointKey = (endpoint: ApiEndpoint): string => {
  return `${endpoint.method}:${endpoint.path}`;
};

/**
 * 检测参数是否有变化
 */
const hasParametersChanged = (oldParams: any[] = [], newParams: any[] = []): boolean => {
  if (oldParams.length !== newParams.length) {
    return true;
  }
  
  // 简单对比参数 JSON 字符串
  const oldStr = JSON.stringify(oldParams.map(p => ({
    name: p.name,
    in: p.in,
    required: p.required,
    type: p.type,
  })).sort());
  const newStr = JSON.stringify(newParams.map(p => ({
    name: p.name,
    in: p.in,
    required: p.required,
    type: p.type,
  })).sort());
  
  return oldStr !== newStr;
};

/**
 * 检测响应是否有变化
 */
const hasResponsesChanged = (oldResponses: any = {}, newResponses: any = {}): boolean => {
  // 简单对比响应状态码
  const oldKeys = Object.keys(oldResponses).sort();
  const newKeys = Object.keys(newResponses).sort();
  
  if (oldKeys.length !== newKeys.length) {
    return true;
  }
  
  return oldKeys.some((key, index) => key !== newKeys[index]);
};

/**
 * 检测 API 端点是否有变化
 */
const detectEndpointChanges = (oldEndpoint: ApiEndpoint, newEndpoint: ApiEndpoint): string[] => {
  const changes: string[] = [];
  
  if (oldEndpoint.summary !== newEndpoint.summary) {
    changes.push('摘要已修改');
  }
  
  if (oldEndpoint.description !== newEndpoint.description) {
    changes.push('描述已修改');
  }
  
  if (hasParametersChanged(oldEndpoint.parameters, newEndpoint.parameters)) {
    changes.push('参数已修改');
  }
  
  if (hasResponsesChanged(oldEndpoint.responses, newEndpoint.responses)) {
    changes.push('返回值已修改');
  }
  
  return changes;
};

/**
 * 比较两个 swagger 文件
 */
export const compareSwagger = (oldSwaggerJson: string, newSwaggerJson: string): SwaggerComparisonResult => {
  try {
    const oldSwagger = JSON.parse(oldSwaggerJson) as SwaggerJSON;
    const newSwagger = JSON.parse(newSwaggerJson) as SwaggerJSON;
    
    const oldEndpoints = extractApiEndpoints(oldSwagger);
    const newEndpoints = extractApiEndpoints(newSwagger);
    
    // 创建端点映射
    const oldEndpointMap = new Map<string, ApiEndpoint>();
    oldEndpoints.forEach(ep => oldEndpointMap.set(getEndpointKey(ep), ep));
    
    const newEndpointMap = new Map<string, ApiEndpoint>();
    newEndpoints.forEach(ep => newEndpointMap.set(getEndpointKey(ep), ep));
    
    // 检测新增的接口
    const added: ApiEndpoint[] = [];
    newEndpoints.forEach(ep => {
      if (!oldEndpointMap.has(getEndpointKey(ep))) {
        added.push(ep);
      }
    });
    
    // 检测删除的接口
    const removed: ApiEndpoint[] = [];
    oldEndpoints.forEach(ep => {
      if (!newEndpointMap.has(getEndpointKey(ep))) {
        removed.push(ep);
      }
    });
    
    // 检测修改的接口
    const modified: { endpoint: ApiEndpoint; changes: string[] }[] = [];
    newEndpoints.forEach(newEp => {
      const key = getEndpointKey(newEp);
      const oldEp = oldEndpointMap.get(key);
      if (oldEp) {
        const changes = detectEndpointChanges(oldEp, newEp);
        if (changes.length > 0) {
          modified.push({
            endpoint: newEp,
            changes,
          });
        }
      }
    });
    
    return { added, removed, modified };
  } catch (error) {
    throw new Error(`Failed to compare swagger files: ${(error as Error).message}`);
  }
};

/**
 * 生成对比结果的 Markdown 表格
 */
export const generateComparisonMarkdown = (result: SwaggerComparisonResult): string => {
  const sections: string[] = [];
  
  // 新增接口
  if (result.added.length > 0) {
    sections.push('### 新增接口\n');
    sections.push('| 方法 | 路径 | 描述 |');
    sections.push('|------|------|------|');
    result.added.forEach(ep => {
      sections.push(`| ${ep.method} | ${ep.path} | ${ep.description || ep.summary || '-'} |`);
    });
    sections.push('');
  }
  
  // 删除接口
  if (result.removed.length > 0) {
    sections.push('### 删除接口\n');
    sections.push('| 方法 | 路径 | 描述 |');
    sections.push('|------|------|------|');
    result.removed.forEach(ep => {
      sections.push(`| ${ep.method} | ${ep.path} | ${ep.description || ep.summary || '-'} |`);
    });
    sections.push('');
  }
  
  // 修改接口
  if (result.modified.length > 0) {
    sections.push('### 修改接口\n');
    sections.push('| 方法 | 路径 | 描述 | 变化 |');
    sections.push('|------|------|------|------|');
    result.modified.forEach(({ endpoint, changes }) => {
      sections.push(`| ${endpoint.method} | ${endpoint.path} | ${endpoint.description || endpoint.summary || '-'} | ${changes.join(', ')} |`);
    });
    sections.push('');
  }
  
  return sections.join('\n');
};

